import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertCircle,
  Download,
  ExternalLink,
  Inbox,
  Loader2,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '@/admin/components/ui/button';
import { cn } from '@/admin/lib/utils';
import { AppLogo } from '@/assets/logos/app-logo';
import {
  fetchPublicShare,
  fetchMarkdownContent,
  findPreviewImage,
  getCachedMarkdownContent,
  resolveDefaultSelectedItem,
} from '../lib/api';
import type { PublicShare, PublicShareItem } from '../lib/types';
import { upsertMetaTag } from '../lib/head';
import { useWebsiteTitlebar } from '../lib/titlebar';
import { AudioView } from './share-audio-player';
import { FileNav, MobileFilePicker, TabletFileStrip } from './share-file-nav';
import { VideoView } from './share-video-player';
import {
  EmptyState,
  LoadingSkeleton,
  ShareFilenameLabel,
} from './share-viewer-shared';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; share: PublicShare };

type PreviewLayout = 'split' | 'focused';

export function SharePage(): React.JSX.Element {
  const { publicId = '', agentId = '', shareId = '' } = useParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [markdownBody, setMarkdownBody] = useState<string>('');
  const [markdownError, setMarkdownError] = useState<string>('');

  useWebsiteTitlebar({
    light: '#fafafa',
    dark: '#fafafa',
    colorScheme: 'light',
  });

  useEffect(() => {
    let cancelled = false;

    setState({ status: 'loading' });
    setSelectedItemId('');
    setMarkdownBody('');
    setMarkdownError('');

    fetchPublicShare({ publicId, agentId, shareId })
      .then(share => {
        if (cancelled) return;
        setState({ status: 'ready', share });
        const initialItem = resolveDefaultSelectedItem(share.items);
        setSelectedItemId(initialItem?.id ?? '');
      })
      .catch(error => {
        if (cancelled) return;
        setState({
          status: 'error',
          message:
            error instanceof Error ? error.message : 'Failed to load share',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, publicId, shareId]);

  const share = state.status === 'ready' ? state.share : null;
  const selectedItem =
    share?.items.find(item => item.id === selectedItemId) ??
    (share ? resolveDefaultSelectedItem(share.items) : null);
  const markdownUrl =
    selectedItem?.kind === 'markdown' ? selectedItem.content_url : undefined;

  useEffect(() => {
    if (!share) {
      updateShareMetadata({
        title: 'Bridge Share',
        description: 'Shared files from Bridge',
        image: null,
      });
      return;
    }

    const ogImage: string | null = findPreviewImage(share.items);

    updateShareMetadata({
      title: share.title,
      description: describeShare(share),
      image: ogImage,
    });
  }, [share]);

  useEffect(() => {
    if (!markdownUrl) {
      setMarkdownBody('');
      setMarkdownError('');
      return;
    }

    const cachedBody = getCachedMarkdownContent(markdownUrl);
    if (cachedBody !== undefined) {
      setMarkdownBody(cachedBody);
      setMarkdownError('');
      return;
    }

    const controller = new AbortController();
    setMarkdownBody('');
    setMarkdownError('');

    fetchMarkdownContent(markdownUrl, controller.signal)
      .then(text => {
        setMarkdownBody(text);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setMarkdownError(
          error instanceof Error ? error.message : 'Failed to load markdown'
        );
      });

    return () => {
      controller.abort();
    };
  }, [markdownUrl]);

  const { sidebarWidth, dragHandleProps, isDragging } = useSidebarResize(240);

  if (state.status === 'loading') {
    return <LoadingSkeleton />;
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        icon={<AlertCircle className="size-[18px]" strokeWidth={2} />}
        title="Share unavailable"
        description={state.message}
      />
    );
  }

  if (!share || share.items.length === 0 || !selectedItem) {
    return (
      <EmptyState
        icon={<Inbox className="size-[18px]" strokeWidth={2} />}
        title="Nothing to show"
        description="This share does not contain any renderable items."
      />
    );
  }

  const singleFocusedKind =
    share.items.length === 1 &&
    (selectedItem.kind === 'audio' || selectedItem.kind === 'video');
  const layout: PreviewLayout = singleFocusedKind ? 'focused' : 'split';
  const folderLabel = resolveFolderLabel(share);
  const showSidebar = layout === 'split' && share.items.length > 1;

  return (
    <div
      className={cn(
        'share-theme flex h-[100dvh] flex-col bg-secondary',
        isDragging && 'select-none'
      )}
    >
      <NavBar
        publicId={share.public_id}
        title={share.title}
        item={selectedItem}
        itemCount={share.items.length}
      />

      {layout === 'split' ? (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {showSidebar ? (
            <>
              {/* Tablet: horizontal file strip (md–lg) */}
              <TabletFileStrip
                items={share.items}
                selectedItemId={selectedItemId}
                onSelectItem={setSelectedItemId}
              />

              {/* Desktop: vertical sidebar (lg+) */}
              <aside
                className="relative z-10 hidden shrink-0 bg-[#fafafa] lg:block"
                style={{ width: sidebarWidth }}
              >
                <FileNav
                  folderLabel={folderLabel}
                  items={share.items}
                  selectedItemId={selectedItemId}
                  onSelectItem={setSelectedItemId}
                />
                <div
                  {...dragHandleProps}
                  className={cn(
                    'group absolute bottom-4.5 top-4.5 -right-[3px] z-20 flex w-[6px] cursor-col-resize touch-none items-stretch justify-center',
                    'outline-none focus-visible:ring-2 focus-visible:ring-[#0077ff]/35 focus-visible:ring-offset-0'
                  )}
                  aria-label="Resize sidebar"
                  role="separator"
                  tabIndex={0}
                >
                  <div
                    className={cn(
                      'h-auto w-[3px] rounded-full transition-opacity duration-150',
                      isDragging
                        ? 'bg-[#0077ff] opacity-100'
                        : 'bg-[#c0c0c0] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                    )}
                  />
                </div>
              </aside>
            </>
          ) : null}

          {/* Phone: native select picker (<md) */}
          <MobileFilePicker
            items={share.items}
            selectedItem={selectedItem}
            onSelectItem={setSelectedItemId}
          />

          <SharePreviewShell hasSidebar={showSidebar}>
            <ContentView
              item={selectedItem}
              markdownBody={markdownBody}
              markdownError={markdownError}
              layout={layout}
            />
          </SharePreviewShell>
        </div>
      ) : (
        <SharePreviewShell>
          <ContentView
            item={selectedItem}
            markdownBody={markdownBody}
            markdownError={markdownError}
            layout={layout}
          />
        </SharePreviewShell>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar resize
// ---------------------------------------------------------------------------

const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 400;

function useSidebarResize(initialWidth: number) {
  const [width, setWidth] = useState(() => {
    try {
      const stored = sessionStorage.getItem('share-sidebar-w');
      if (stored) {
        const n = Number(stored);
        if (n >= SIDEBAR_MIN && n <= SIDEBAR_MAX) return n;
      }
    } catch {
      /* ignore */
    }
    return initialWidth;
  });
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const latestWidthRef = useRef(width);
  const captureTargetRef = useRef<HTMLElement | null>(null);
  latestWidthRef.current = width;

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: PointerEvent) => {
      const delta = e.clientX - startXRef.current;
      const next = Math.round(
        Math.min(
          SIDEBAR_MAX,
          Math.max(SIDEBAR_MIN, startWidthRef.current + delta)
        )
      );
      setWidth(next);
    };

    const finish = (e: PointerEvent) => {
      const el = captureTargetRef.current;
      if (el) {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        captureTargetRef.current = null;
      }
      setIsDragging(false);
      try {
        sessionStorage.setItem(
          'share-sidebar-w',
          String(latestWidthRef.current)
        );
      } catch {
        /* ignore */
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
    };
  }, [isDragging]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      captureTargetRef.current = e.currentTarget;
      e.currentTarget.setPointerCapture(e.pointerId);
      startXRef.current = e.clientX;
      startWidthRef.current = latestWidthRef.current;
      setIsDragging(true);
    },
    []
  );

  const KEYBOARD_STEP = 10;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
          next = Math.min(SIDEBAR_MAX, width + KEYBOARD_STEP);
          break;
        case 'ArrowLeft':
          next = Math.max(SIDEBAR_MIN, width - KEYBOARD_STEP);
          break;
        case 'Home':
          next = SIDEBAR_MIN;
          break;
        case 'End':
          next = SIDEBAR_MAX;
          break;
        default:
          return;
      }
      e.preventDefault();
      setWidth(next);
      try {
        sessionStorage.setItem('share-sidebar-w', String(next));
      } catch {
        /* ignore */
      }
    },
    [width]
  );

  return {
    sidebarWidth: width,
    isDragging,
    dragHandleProps: {
      onPointerDown: handlePointerDown,
      onKeyDown: handleKeyDown,
      'aria-valuenow': width,
      'aria-valuemin': SIDEBAR_MIN,
      'aria-valuemax': SIDEBAR_MAX,
      'aria-orientation': 'vertical' as const,
    },
  };
}

function buildPublicAssetAPIURL(publicId: string, contentURL: string): string {
  let parsed: URL;
  try {
    parsed = new URL(contentURL, window.location.origin);
  } catch {
    return contentURL;
  }
  if (parsed.pathname.includes('/v1/public/agent-shares/')) {
    return parsed.toString();
  }
  const segments = parsed.pathname.split('/').filter(Boolean);
  const assetId = segments.at(-2);
  const filename = segments.at(-1);
  if (!assetId || !filename) return contentURL;
  return `/v1/public/shares/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}/${encodeURIComponent(filename)}`;
}

function resolveShareImageContentURL(item: PublicShareItem): string {
  return item.content_url ?? '';
}

function resolveWebpageArchiveIframeSrc(item: PublicShareItem): string {
  return item.content_url ?? '';
}

function resolveShareItemDownloadURL(
  publicId: string,
  item: PublicShareItem
): string | null {
  if (!item.content_url) return null;
  return buildPublicAssetAPIURL(publicId, item.content_url);
}

// ---------------------------------------------------------------------------
// Nav
// ---------------------------------------------------------------------------

function NavBar(props: {
  publicId: string;
  title: string;
  item: PublicShareItem | null;
  itemCount: number;
}): React.JSX.Element {
  const openOriginalHref = props.item?.content_url ?? null;

  const downloadURL = props.item?.content_url
    ? resolveShareItemDownloadURL(props.publicId, props.item)
    : null;

  return (
    <header className="flex h-12 shrink-0 items-center px-4">
      <a
        href="/"
        className="flex shrink-0 items-center gap-2 transition-opacity hover:opacity-70"
        aria-label="Home"
      >
        <AppLogo className="size-[20px] text-[#1a1a1a]" />
        <span className="text-[15px] font-semibold text-[#1a1a1a]">Bridge</span>
      </a>

      <span className="mx-2 text-[15px] text-[#d0d0d0]">/</span>

      <span className="min-w-0 truncate text-[15px] text-[#8a8a8a]">
        {props.title}
      </span>

      <div className="ml-auto flex items-center gap-2">
        {openOriginalHref ? (
          <a
            href={openOriginalHref}
            target="_blank"
            rel="noreferrer"
            aria-label="Open original file"
            className="flex size-8 items-center justify-center rounded-lg text-[#8a8a8a] transition-colors hover:bg-[#f0f0f0] hover:text-[#1a1a1a]"
          >
            <ExternalLink className="size-[16px]" strokeWidth={2} />
          </a>
        ) : null}
        {downloadURL ? (
          <Button
            variant="default"
            size="sm"
            className="h-7 gap-1 px-2.5 text-xs font-medium has-[>svg]:px-2 active:translate-y-px"
            asChild
          >
            <a
              href={downloadURL}
              download={props.item?.original_filename ?? undefined}
            >
              <Download className="size-3.5" strokeWidth={2} aria-hidden />
              Download Bridge
            </a>
          </Button>
        ) : null}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

function SharePreviewShell(props: {
  children: React.ReactNode;
  hasSidebar?: boolean;
}): React.JSX.Element {
  return (
    <main
      className={cn(
        'flex min-h-0 min-w-0 flex-1 bg-[#fafafa] px-2 pb-2 pt-0 md:pt-2',
        props.hasSidebar && 'lg:pl-0'
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto rounded-[16px] border border-[#ebebeb] bg-white text-[12px] shadow-[0_1px_3px_0_rgba(0,0,0,0.05)]">
        {props.children}
      </div>
    </main>
  );
}

function ContentView(props: {
  item: PublicShareItem;
  markdownBody: string;
  markdownError: string;
  layout: PreviewLayout;
}): React.JSX.Element {
  switch (props.item.kind) {
    case 'markdown':
      return (
        <MarkdownView body={props.markdownBody} error={props.markdownError} />
      );
    case 'image':
      return <ImageView item={props.item} />;
    case 'audio':
      return <AudioView item={props.item} />;
    case 'video':
      return <VideoView item={props.item} />;
    case 'webpage_archive':
      return <WebpageView item={props.item} />;
    default:
      return (
        <EmptyState
          icon={<Inbox className="size-[18px]" strokeWidth={2} />}
          title="Unsupported item"
          description={`Cannot render "${props.item.kind}" in this viewer yet.`}
        />
      );
  }
}

function MarkdownView(props: {
  body: string;
  error: string;
}): React.JSX.Element {
  if (props.error) {
    return (
      <EmptyState
        icon={<AlertCircle className="size-[18px]" strokeWidth={2} />}
        title="Failed to load"
        description={props.error}
      />
    );
  }
  if (!props.body) {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <Loader2
          className="size-5 animate-spin text-[#8a8a8a]"
          strokeWidth={2}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 justify-center">
      <article
        data-testid="share-preview-markdown"
        className="share-prose w-full max-w-[720px] px-8 py-10 sm:px-12 sm:py-14 md:px-16"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children, ...tableProps }) => (
              <div className="share-prose-table-scroll">
                <table {...tableProps}>{children}</table>
              </div>
            ),
          }}
        >
          {props.body}
        </ReactMarkdown>
      </article>
    </div>
  );
}

function ImageView(props: { item: PublicShareItem }): React.JSX.Element {
  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 items-center justify-center p-6 md:p-10">
        <div className="flex max-h-full max-w-4xl flex-col items-center gap-4">
          <div className="max-h-[min(72vh,100%)] max-w-full overflow-hidden rounded-lg border border-[#e8e8e8] bg-[#f5f5f5]">
            <img
              data-testid="share-preview-image"
              src={resolveShareImageContentURL(props.item)}
              alt={props.item.display_name}
              className="block max-h-[72vh] min-h-[48px] min-w-[48px] max-w-full object-contain"
            />
          </div>
          <div className="flex w-full max-w-sm min-w-0 justify-center">
            <ShareFilenameLabel
              name={props.item.display_name}
              textClassName="text-[13px] text-[#8a8a8a]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function WebpageView(props: {
  item: PublicShareItem;
}): React.JSX.Element {
  const iframeSrc = resolveWebpageArchiveIframeSrc(props.item);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [iframeSrc]);

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-white">
      <div
        data-testid="share-preview-webpage"
        className="relative min-h-0 flex-1 overflow-hidden bg-white"
        aria-busy={!isLoaded}
      >
        {!isLoaded ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white text-[#8a8a8a]"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="size-5 animate-spin" strokeWidth={2} />
            <span className="sr-only">Loading webpage preview…</span>
          </div>
        ) : null}
        <iframe
          title={props.item.display_name}
          src={iframeSrc}
          sandbox="allow-scripts"
          onLoad={() => setIsLoaded(true)}
          className={cn(
            'size-full border-0 bg-white transition-opacity duration-150',
            !isLoaded && 'opacity-0'
          )}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveFolderLabel(share: PublicShare): string {
  const folderPaths = share.items
    .map(item => parentPath(item.relative_path))
    .filter(Boolean);
  const commonPath = findCommonPath(folderPaths);
  return commonPath || share.title;
}

function parentPath(relativePath: string): string {
  const normalized = relativePath.trim().replace(/^\/+|\/+$/g, '');
  const segments = normalized.split('/').filter(Boolean);
  if (segments.length <= 1) return '';
  return segments.slice(0, -1).join('/');
}

function findCommonPath(paths: string[]): string {
  if (paths.length === 0) return '';

  const initial = paths[0]?.split('/').filter(Boolean) ?? [];
  let common = initial;

  for (const currentPath of paths.slice(1)) {
    const current = currentPath.split('/').filter(Boolean);
    let index = 0;
    while (
      index < common.length &&
      index < current.length &&
      common[index] === current[index]
    ) {
      index += 1;
    }
    common = common.slice(0, index);
    if (common.length === 0) return '';
  }

  return common.join('/');
}

function describeShare(share: PublicShare): string {
  const parts = [
    share.counts.markdown_count
      ? `${share.counts.markdown_count} Markdown`
      : '',
    share.counts.image_count ? `${share.counts.image_count} image` : '',
    share.counts.audio_count ? `${share.counts.audio_count} audio` : '',
    share.counts.video_count ? `${share.counts.video_count} video` : '',
    share.counts.webpage_count ? `${share.counts.webpage_count} webpage` : '',
  ].filter(Boolean);

  const dateLabel = new Date(share.created_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return `${parts.join(' / ')} · ${dateLabel}`;
}

function updateShareMetadata(input: {
  title: string;
  description: string;
  image: string | null;
}) {
  document.title = input.title;

  updateMetaTag('name', 'description', input.description);
  updateMetaTag('property', 'og:title', input.title);
  updateMetaTag('property', 'og:description', input.description);
  updateMetaTag('property', 'og:type', 'website');
  updateMetaTag(
    'name',
    'twitter:card',
    input.image ? 'summary_large_image' : 'summary'
  );
  if (input.image) {
    updateMetaTag('property', 'og:image', input.image);
  }
}

function updateMetaTag(
  attribute: 'name' | 'property',
  key: string,
  content: string
) {
  upsertMetaTag(attribute, key, content);
}
